import { yupResolver } from '@hookform/resolvers/yup';
import type {
  DialogContentProps,
  DialogTriggerProps,
} from '@radix-ui/react-dialog';
import { useMutation, useQuery } from '@tanstack/react-query';
import Link from 'next/link';
import { useRouter } from 'next/router';
import type { PropsWithChildren } from 'react';
import { useEffect, useRef, useState } from 'react';
import { Controller } from 'react-hook-form';
import toast from 'react-hot-toast';
import * as yup from 'yup';

import useForm from '@/hooks/useForm';
import { useRecaptchaV3 } from '@/hooks/useRecaptchaV3';
import { queries } from '@/queries';
import { contactUs } from '@/services/common';

import Button from '../Button';
import Checkbox from '../Form/Fields/Checkbox';
import MultiLineInput from '../Form/Fields/MultiLineInput';
import TextInputField from '../Form/Fields/TextInputField';
import { Select } from '../Select';
import Text from '../Text';
import { PhoneInput } from '../ui/phone-input';
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from '../ui/sheet';

const ContactFormSheet = ({
  sheetTriggerProps,
  sheetContentProps,
  children,
  modalParam,
}: PropsWithChildren<{
  sheetTriggerProps?: DialogTriggerProps;
  sheetContentProps?: DialogContentProps;
  modalParam?: string;
}>) => {
  const { query, replace, pathname } = useRouter();
  const phoneInputPortalRef = useRef<HTMLDivElement | null>(null);
  const [openSheet, setOpenSheet] = useState(false);

  const { verifyRecaptchaV3, isLoading: recaptchaLoading } = useRecaptchaV3({
    action: 'contactFormSheet',
  });

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    reset: resetForm,
  } = useForm<yup.InferType<typeof schema>>({
    resolver: yupResolver(schema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      phoneNumber: '',
      jobTitle: '',
      companyName: '',
      country: undefined,
      message: '',
      smsAccountConsent: false,
      smsMarketingConsent: false,
    },
  });

  const { mutate, isLoading, reset } = useMutation({
    mutationFn: contactUs,
    mutationKey: ['contactUs'],
    onSuccess: () => {
      setOpenSheet(false);
      reset();
      resetForm();
      toast.success(
        'Your message was sent successfully. Thank you for choosing MindPal'
      );
    },
  });

  const { data: countriesData } = useQuery({
    ...queries.common.countries(undefined),
    meta: { silent: true },
  });

  const countriesOptions = countriesData?.results.map((country) => ({
    value: country.id,
    label: country.name,
  }));

  useEffect(() => {
    if (modalParam && query[modalParam] === 'true') {
      setOpenSheet(true);
    }
  }, [modalParam, query]);

  const handleSheetOpenChange = (open: boolean) => {
    setOpenSheet(open);

    if (!modalParam) return;

    const routerQuery = { ...query };

    if (open) {
      routerQuery[modalParam] = 'true';
    } else {
      delete routerQuery[modalParam];
    }

    replace(
      {
        pathname,
        query: routerQuery,
      },
      undefined,
      { shallow: true }
    );
  };

  return (
    <Sheet open={openSheet} onOpenChange={handleSheetOpenChange}>
      <SheetTrigger {...sheetTriggerProps}>{children}</SheetTrigger>
      <SheetContent
        {...sheetContentProps}
        className="w-full !max-w-none overflow-auto lg:w-1/2"
      >
        <div ref={phoneInputPortalRef} />
        <div className="flex flex-col gap-4">
          <SheetTitle className="font-bold">
            Let&apos;s start working{' '}
            <Text variant="h3" as="strong" className="text-jb-primary-500">
              together!
            </Text>
          </SheetTitle>
          <Text variant="body" className="text-neutral-700">
            Tell us what you’re building - and how we can help you deliver it{' '}
            <br />
            Take the first step by filling out a short form.
          </Text>
        </div>
        <form
          className="flex flex-col gap-4 pt-10"
          onSubmit={(e) =>
            handleSubmit(async (formData) => {
              const { isVerified } = await verifyRecaptchaV3(e);
              if (isVerified) mutate({ ...formData, source: 'LANDING' });
            })(e)
          }
        >
          <TextInputField
            {...register('firstName')}
            variant="light"
            sizeVariant="xs"
            containerClassName="!gap-0"
            label="First Name"
            labelClassName="text-sm text-neutral-1000"
            error={errors.firstName?.message}
          />
          <TextInputField
            {...register('lastName')}
            variant="light"
            sizeVariant="xs"
            containerClassName="!gap-0"
            label="Last Name"
            labelClassName="text-sm text-neutral-1000"
            error={errors.lastName?.message}
          />
          <div className="flex flex-col gap-3 lg:flex-row">
            <TextInputField
              {...register('email')}
              variant="light"
              sizeVariant="xs"
              containerClassName="!gap-0"
              label="Business email address"
              labelClassName="text-sm text-neutral-1000"
              error={errors.email?.message}
            />
            <div className="flex-1 text-neutral-1000">
              <Controller
                control={control}
                name="phoneNumber"
                render={({ field }) => (
                  <PhoneInput
                    labelClassName="text-sm"
                    className="h-10 items-stretch justify-stretch"
                    selectButtonClassName="bg-neutral-100 border-neutral-300"
                    popoverContainer={() => phoneInputPortalRef.current}
                    error={errors.phoneNumber?.message}
                    {...field}
                  />
                )}
              />
            </div>
          </div>
          <TextInputField
            {...register('jobTitle')}
            variant="light"
            sizeVariant="xs"
            containerClassName="!gap-0"
            label="Job Title"
            labelClassName="text-sm text-neutral-1000"
            error={errors.jobTitle?.message}
          />
          <TextInputField
            {...register('companyName')}
            variant="light"
            sizeVariant="xs"
            containerClassName="!gap-0"
            label="Company / Institution"
            labelClassName="text-sm text-neutral-1000"
            error={errors.companyName?.message}
          />
          <Controller
            control={control}
            name="country"
            render={({ field: { onChange, value } }) => (
              <div>
                <Select
                  isClearable
                  variant="lightGray"
                  sizeVariant="sm"
                  label="Country"
                  labelClassName="text-sm text-neutral-1000"
                  value={
                    countriesOptions?.find(
                      (country) => country.value === value
                    ) ?? null
                  }
                  onChange={(newValue) => onChange(newValue?.value)}
                  options={countriesOptions}
                  error={errors.country?.message}
                  className="flex-1 !gap-0 text-body-small"
                />
              </div>
            )}
          />
          <MultiLineInput
            {...register('message')}
            variant="light"
            sizeVariant="xs"
            rows={4}
            className="!border-neutral-300"
            containerClassName="!gap-0"
            label="Tell us about your project, a bit of context will allow us to connect you to the right team faster:"
            labelClassName="text-sm text-neutral-1000"
            error={errors.message?.message}
          />
          <div>
            <Checkbox
              {...register('smsAccountConsent')}
              error={errors.smsAccountConsent}
            >
              <Text variant="body-small" className="text-neutral-1000">
                I agree to receive customer care and account-related text
                messages from MindPal. Msg freq varies, up to 4 msgs/month. Msg
                &amp; data rates may apply. Reply STOP to opt out, HELP for
                help. See{' '}
                <Link className="underline" href="/sms-terms" target="_blank">
                  SMS Terms
                </Link>{' '}
                &amp;{' '}
                <Link
                  className="underline"
                  href="/privacy-policy"
                  target="_blank"
                >
                  Privacy Policy
                </Link>
                .
              </Text>
            </Checkbox>
            <Checkbox
              {...register('smsMarketingConsent')}
              error={errors.smsMarketingConsent}
            >
              <Text variant="body-small" className="text-neutral-1000">
                I agree to receive marketing text messages from MindPal. Msg
                freq varies, up to 8 msgs/month. Msg &amp; data rates may apply.
                Reply STOP to opt out, HELP for help. See{' '}
                <Link className="underline" href="/sms-terms" target="_blank">
                  SMS Terms
                </Link>{' '}
                &amp;{' '}
                <Link
                  className="underline"
                  href="/privacy-policy"
                  target="_blank"
                >
                  Privacy Policy
                </Link>
                .
              </Text>
            </Checkbox>
          </div>
          <Text variant="body-small" className="mb-10 text-neutral-1000">
            You can learn more about how we use and protect your information in
            our{' '}
            <Link className="underline " href="/privacy-policy" target="_blank">
              Privacy Policy
            </Link>
            .
          </Text>
          <Button
            variant="primaryLight"
            type="submit"
            isLoading={isLoading || recaptchaLoading}
          >
            Submit
          </Button>
        </form>
      </SheetContent>
    </Sheet>
  );
};

export default ContactFormSheet;

const schema = yup.object({
  firstName: yup.string().required('This field is required!'),
  lastName: yup.string().required('This field is required!'),
  phoneNumber: yup
    .string()
    .matches(/^[0-9\b +()-]+$/, {
      excludeEmptyString: true,
      message: 'Enter a valid phone number',
    })
    .defined()
    .required('This field is required!'),
  jobTitle: yup.string().required('This field is required!'),
  companyName: yup.string().required('This field is required!'),
  email: yup
    .string()
    .matches(
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
      { excludeEmptyString: true, message: 'Enter a valid email' }
    )
    .defined()
    .required('This field is required!'),
  message: yup.string().required('This field is required!'),
  source: yup.string().oneOf(['LANDING']),
  country: yup.number().nullable().required('This field is required!'),
  smsAccountConsent: yup.boolean().default(false),
  smsMarketingConsent: yup.boolean().default(false),
});
